import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { FaUserEdit, FaUsers, FaHeart, FaComments, FaEye, FaEnvelope, FaHandshake, FaCrown, FaShieldAlt, FaArrowRight, FaStar, FaChevronLeft, FaChevronRight, FaPhoneAlt, FaMapMarkerAlt } from 'react-icons/fa';

/* ------------------------------------------------------------------ */
/*  Circular Progress                                                  */
/* ------------------------------------------------------------------ */
const CircularProgress = ({ percentage }) => {
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  const color = percentage >= 80 ? '#10B981' : percentage >= 50 ? '#F59E0B' : '#DC143C';

  return (
    <div className="relative w-32 h-32">
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={radius} stroke="#e5e7eb" strokeWidth="8" fill="none" />
        <circle
          cx="60" cy="60" r={radius}
          stroke={color}
          strokeWidth="8"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold" style={{ color }}>{percentage}%</span>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Navbar                                                             */
/* ------------------------------------------------------------------ */
const Navbar = () => {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <nav className="bg-gradient-to-r from-[#8B0000] via-[#DC143C] to-[#8B0000] shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" className="text-2xl font-['Noto_Sans_Devanagari'] font-bold text-[#FFD700]">विवाह आरंभ</Link>
        <div className="hidden md:flex items-center gap-6">
          <Link to="/dashboard" className="text-white/90 hover:text-[#FFD700] transition-colors text-sm font-medium">Dashboard</Link>
          <Link to="/browse" className="text-white/90 hover:text-[#FFD700] transition-colors text-sm font-medium">Browse</Link>
          <Link to="/matches" className="text-white/90 hover:text-[#FFD700] transition-colors text-sm font-medium">Matches</Link>
          <Link to="/chat" className="text-white/90 hover:text-[#FFD700] transition-colors text-sm font-medium">Chat</Link>
          <Link to="/my-profile" className="text-white/90 hover:text-[#FFD700] transition-colors text-sm font-medium">My Profile</Link>
          <div className="flex items-center gap-3 ml-4">
            <div className="w-8 h-8 rounded-full bg-[#FFD700] text-[#8B0000] flex items-center justify-center font-bold text-sm">
              {user?.first_name?.charAt(0) || 'U'}
            </div>
            <button onClick={logout} className="text-white/70 hover:text-white text-sm">Logout</button>
          </div>
        </div>
        <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-white text-xl">
          {menuOpen ? '\u2715' : '\u2630'}
        </button>
      </div>
      {menuOpen && (
        <div className="md:hidden bg-[#8B0000] px-6 pb-4 space-y-2">
          <Link to="/dashboard" className="block text-white/90 py-2 text-sm">Dashboard</Link>
          <Link to="/browse" className="block text-white/90 py-2 text-sm">Browse</Link>
          <Link to="/matches" className="block text-white/90 py-2 text-sm">Matches</Link>
          <Link to="/chat" className="block text-white/90 py-2 text-sm">Chat</Link>
          <Link to="/my-profile" className="block text-white/90 py-2 text-sm">My Profile</Link>
          <button onClick={logout} className="text-white/70 text-sm py-2">Logout</button>
        </div>
      )}
    </nav>
  );
};

/* ------------------------------------------------------------------ */
/*  Footer                                                             */
/* ------------------------------------------------------------------ */
const Footer = () => (
  <footer className="bg-[#8B0000] text-white mt-auto">
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h3 className="text-xl font-['Noto_Sans_Devanagari'] font-bold text-[#FFD700]">विवाह आरंभ</h3>
          <p className="text-white/60 text-sm">Premium Hindu Vanjari Matrimonial Platform</p>
        </div>
        <div className="flex gap-6 text-sm text-white/70">
          <Link to="/about" className="hover:text-[#FFD700]">About</Link>
          <Link to="/pricing" className="hover:text-[#FFD700]">Pricing</Link>
          <a href="#" className="hover:text-[#FFD700]">Privacy</a>
          <a href="#" className="hover:text-[#FFD700]">Terms</a>
        </div>
        <p className="text-white/50 text-xs">&copy; 2026 Vivah Arambh by Lyftek Solutions</p>
      </div>
    </div>
  </footer>
);

/* ------------------------------------------------------------------ */
/*  Match Card                                                         */
/* ------------------------------------------------------------------ */
const MatchCard = ({ match }) => (
  <div className="flex-shrink-0 w-56 bg-white rounded-2xl shadow-md border border-[#FFD700]/20 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
    <div className="h-48 bg-gradient-to-br from-[#DC143C]/10 to-[#FFD700]/10 flex items-center justify-center">
      {match.profile_photo ? (
        <img src={match.profile_photo} alt={match.first_name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#DC143C] to-[#8B0000] text-white flex items-center justify-center text-2xl font-bold">
          {match.first_name?.charAt(0) || '?'}
        </div>
      )}
    </div>
    <div className="p-4">
      <h4 className="font-semibold text-[#8B0000] truncate">{match.first_name} {match.last_name}</h4>
      <p className="text-xs text-gray-500">{match.age || '?'} yrs, {match.city || 'N/A'}</p>
      <p className="text-xs text-gray-500">{match.qualification || ''}</p>
      {match.match_score && (
        <div className="mt-2 flex items-center gap-1">
          <FaStar className="text-[#FFD700] text-xs" />
          <span className="text-xs text-[#B8860B] font-semibold">{match.match_score}% Match</span>
        </div>
      )}
      <Link
        to={`/profile/${match._id || match.id}`}
        className="mt-3 block text-center text-sm py-1.5 bg-gradient-to-r from-[#DC143C] to-[#8B0000] text-white rounded-lg hover:shadow-md transition-all"
      >
        View Profile
      </Link>
    </div>
  </div>
);

/* ================================================================== */
/*  DASHBOARD PAGE                                                     */
/* ================================================================== */
const DashboardPage = () => {
  const { user, isPremium } = useAuth();
  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState({ views: 0, interests: 0, mutual: 0 });
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [scrollPos, setScrollPos] = useState(0);

  /* ---- Greeting based on time ---- */
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'सुप्रभात';
    if (hour < 17) return 'नमस्कार';
    return 'शुभ संध्याकाळ';
  };

  /* ---- Profile completion ---- */
  const getProfileCompletion = () => {
    if (!user) return 0;
    const fields = [
      'first_name', 'last_name', 'gender', 'dob', 'mobile', 'email',
      'caste', 'city', 'height_cm', 'qualification', 'occupation',
      'bio', 'profile_photo',
    ];
    const filled = fields.filter((f) => user[f] && String(user[f]).trim() !== '').length;
    return Math.round((filled / fields.length) * 100);
  };

  /* ---- Fetch data ---- */
  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const res = await axios.get('/api/search/smart-match');
        setMatches(res.data.matches || res.data || []);
      } catch (err) {
        // Silently handle - may not be logged in or endpoint not ready
        setMatches([]);
      } finally {
        setLoadingMatches(false);
      }
    };

    const fetchStats = async () => {
      try {
        const res = await axios.get('/api/users/stats');
        setStats(res.data || { views: 0, interests: 0, mutual: 0 });
      } catch (err) {
        // Use defaults
      }
    };

    fetchMatches();
    fetchStats();
  }, []);

  const completion = getProfileCompletion();

  const quickActions = [
    { icon: <FaUserEdit size={22} />, label: 'Edit Profile', marathi: 'प्रोफाइल संपादा', link: '/my-profile', color: 'from-[#DC143C] to-[#8B0000]' },
    { icon: <FaUsers size={22} />, label: 'Browse Profiles', marathi: 'प्रोफाइल शोधा', link: '/browse', color: 'from-[#B8860B] to-[#8B6914]' },
    { icon: <FaHeart size={22} />, label: 'View Matches', marathi: 'जुळण्या पहा', link: '/matches', color: 'from-[#E91E63] to-[#C2185B]' },
    { icon: <FaComments size={22} />, label: 'Chat', marathi: 'चॅट करा', link: '/chat', color: 'from-[#7B1FA2] to-[#4A148C]' },
  ];

  const scrollCarousel = (dir) => {
    const container = document.getElementById('match-carousel');
    if (!container) return;
    const amount = dir === 'left' ? -240 : 240;
    container.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000]">
              {getGreeting()}, {user?.first_name || 'User'}!
            </h1>
            <p className="text-gray-600 mt-1">Welcome to your Vivah Arambh dashboard</p>
          </div>
          <div className="flex items-center gap-4 bg-white rounded-2xl p-5 shadow-md border border-[#FFD700]/20">
            <CircularProgress percentage={completion} />
            <div>
              <p className="text-sm font-semibold text-[#8B0000]">Profile Completion</p>
              <p className="text-xs text-gray-500 mt-1">
                {completion < 100 ? 'Complete your profile for better matches' : 'Great! Your profile is complete'}
              </p>
              {completion < 100 && (
                <Link to="/my-profile" className="text-xs text-[#DC143C] font-semibold hover:underline mt-1 inline-block">
                  Complete Now →
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Subscription banner */}
        {!isPremium && (
          <div className="bg-gradient-to-r from-[#8B0000] via-[#DC143C] to-[#8B0000] rounded-2xl p-6 mb-8 flex flex-col md:flex-row items-center justify-between gap-4 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#FFD700] flex items-center justify-center">
                <FaCrown className="text-[#8B0000] text-xl" />
              </div>
              <div>
                <p className="text-white font-bold text-lg">Upgrade to Premium</p>
                <p className="text-white/70 text-sm">Get unlimited contacts, smart matching & more</p>
              </div>
            </div>
            <Link
              to="/pricing"
              className="bg-[#FFD700] text-[#8B0000] px-6 py-2.5 rounded-full font-bold hover:bg-[#FFF8DC] transition-all hover:-translate-y-0.5"
            >
              Upgrade Now
            </Link>
          </div>
        )}

        {/* Verification banner */}
        {user && !user.is_verified && (
          <div className="bg-white rounded-2xl p-5 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-md border border-[#FFD700]/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                <FaShieldAlt className="text-blue-500" />
              </div>
              <div>
                <p className="font-semibold text-[#8B0000]">Get Verified</p>
                <p className="text-xs text-gray-500">Verified profiles get 3x more responses</p>
              </div>
            </div>
            <Link
              to="/aadhaar-verification"
              className="text-sm bg-gradient-to-r from-[#DC143C] to-[#8B0000] text-white px-5 py-2 rounded-lg font-semibold hover:shadow-md transition-all"
            >
              Verify Now
            </Link>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {quickActions.map((a, i) => (
            <Link
              key={i}
              to={a.link}
              className="bg-white rounded-2xl p-5 shadow-md border border-[#FFD700]/10 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-center group"
            >
              <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br ${a.color} text-white flex items-center justify-center group-hover:scale-110 transition-transform`}>
                {a.icon}
              </div>
              <p className="text-sm font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000]">{a.marathi}</p>
              <p className="text-xs text-gray-500">{a.label}</p>
            </Link>
          ))}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            { icon: <FaEye />, count: stats.views, label: 'Profile Views', marathi: 'प्रोफाइल व्ह्यूज', color: 'text-blue-500', bg: 'bg-blue-50' },
            { icon: <FaEnvelope />, count: stats.interests, label: 'Interests Received', marathi: 'स्वारस्य प्राप्त', color: 'text-pink-500', bg: 'bg-pink-50' },
            { icon: <FaHandshake />, count: stats.mutual, label: 'Mutual Matches', marathi: 'परस्पर जुळण्या', color: 'text-green-500', bg: 'bg-green-50' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-md border border-[#FFD700]/10 flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl ${s.bg} ${s.color} flex items-center justify-center text-xl`}>
                {s.icon}
              </div>
              <div>
                <p className="text-2xl font-bold text-[#8B0000]">{s.count}</p>
                <p className="text-xs text-gray-500">{s.marathi}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Match Carousel */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000]">शिफारस केलेले जुळणे</h2>
              <p className="text-sm text-gray-500">Recommended Matches</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => scrollCarousel('left')} className="w-8 h-8 rounded-full bg-white border border-[#DC143C]/30 text-[#DC143C] flex items-center justify-center hover:bg-[#DC143C] hover:text-white transition-all">
                <FaChevronLeft size={12} />
              </button>
              <button onClick={() => scrollCarousel('right')} className="w-8 h-8 rounded-full bg-white border border-[#DC143C]/30 text-[#DC143C] flex items-center justify-center hover:bg-[#DC143C] hover:text-white transition-all">
                <FaChevronRight size={12} />
              </button>
            </div>
          </div>

          {loadingMatches ? (
            <div className="flex gap-4 overflow-hidden">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex-shrink-0 w-56 h-72 rounded-2xl skeleton" />
              ))}
            </div>
          ) : matches.length > 0 ? (
            <div id="match-carousel" className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin" style={{ scrollbarWidth: 'thin' }}>
              {matches.map((m, i) => (
                <MatchCard key={m._id || m.id || i} match={m} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-10 text-center shadow-md border border-[#FFD700]/10">
              <FaHeart className="mx-auto text-4xl text-[#DC143C]/20 mb-3" />
              <p className="text-gray-500 font-['Noto_Sans_Devanagari']">अद्याप कोणतेही जुळणे नाही</p>
              <p className="text-sm text-gray-400 mt-1">Complete your profile and preferences to get matches</p>
              <Link to="/my-profile" className="inline-flex items-center gap-2 mt-4 text-[#DC143C] font-semibold text-sm hover:underline">
                Complete Profile <FaArrowRight />
              </Link>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-6 shadow-md border border-[#FFD700]/10">
          <h2 className="text-xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000] mb-4">अलीकडील क्रियाकलाप</h2>
          <div className="space-y-3">
            {[
              { text: 'Welcome to Vivah Arambh! Complete your profile to start getting matches.', time: 'Now', icon: <FaStar className="text-[#FFD700]" /> },
              { text: 'Update your partner preferences for better match results.', time: 'Tip', icon: <FaHeart className="text-[#DC143C]" /> },
              { text: 'Verify your profile to boost visibility and trust.', time: 'Tip', icon: <FaShieldAlt className="text-blue-500" /> },
            ].map((a, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg hover:bg-[#FFF8F0] transition-colors">
                <div className="mt-0.5">{a.icon}</div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{a.text}</p>
                  <span className="text-xs text-gray-400">{a.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DashboardPage;
